import { Colors, Lightning } from "@lightningjs/sdk";
import { TranslatableObject, TranslatableText } from "../../data-display/TranslatableText/TranslatableText";
import { Icon } from "../../data-display/Icon/Icon";
import theme from "../../../lib/theme";
import { Theme, themeManager } from "../../configurations/Theme/ThemeManager";

interface ButtonTemplateSpec extends Lightning.Component.TemplateSpec {
    useBorder: boolean;
    isActive: boolean;
    enterAction: any | undefined;
    actionId: string | undefined;
    label: string | TranslatableObject;
    icon: string;
    hasActive: boolean;
    iconWidth: number;
    disabled: boolean;
    reverseColor: boolean;
    fontSize: number;

    Border: object;
    RoundedRectangle: object;
    Content: {
        Label: typeof TranslatableText;
        Icon: typeof Icon;
    };
}

export class Button
    extends Lightning.Component<ButtonTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<ButtonTemplateSpec>
{
    _id: string | undefined;
    _enterAction: any;
    _isSecondary = false;
    _isActive = false;
    _disabled = false;
    _reverseColor = false;

    readonly Content = this.getByRef("Content")!;
    readonly Label = this.Content.getByRef("Label")!;
    readonly Icon = this.Content.getByRef("Icon")!;
    private _themeCallback!: (theme: Theme | undefined) => void;

    static override _template(): Lightning.Component.Template<ButtonTemplateSpec> {
        return {
            h: theme.button.h || 56,
            collision: true,
            zIndex: 30,
            Border: {},
            RoundedRectangle: {
                rect: true
            },
            Content: {
                w: (w) => w,
                h: (h) => h,
                flex: {
                    alignItems: "center",
                    justifyContent: "center"
                },
                Icon: {
                    // w: 24,
                    // h: 33,
                    // w: 33,
                    // iconWidth: 33,
                    // mountY: 0.5,
                    // y: (y) => y / 2,
                    type: Icon,
                    hasActive: true,
                    activeColorValue: theme.colors.text.accentFocus
                },
                Label: {
                    // h: 24,
                    // mountY: 0.5,
                    // y: (h) => h / 2,
                    y: 2,
                    type: TranslatableText,
                    text: {
                        textAlign: "center",
                        fontSize: theme.button.font.size,
                        textColor: Colors(theme.colors.text.primary).get()
                    },
                    alpha: 0
                }
            }
        };
    }

    override _setup() {
        this._createBackground();
    }

    override _detach() {
        themeManager.unsubscribe(this._themeCallback);
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        this.Label.patch({
            text: {
                textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
            }
        });
    }

    set enterAction(value: any) {
        this._enterAction = value;
    }

    set actionId(value: string | undefined) {
        this._id = value;
    }

    get actionId(): string | undefined {
        return this._id;
    }

    set hasActive(value: boolean) {
        if (theme.button.border.enabled) return;

        this.Icon.patch({
            hasActive: value
        });
    }

    set disabled(value: boolean) {
        this._disabled = value;

        this._createBackground();
    }

    set reverseColor(value: boolean) {
        this._reverseColor = value;

        this._createBackground();
    }

    set label(value: string | TranslatableObject) {
        const isString = typeof value === "string";
        this.Label.patch({
            key: isString ? value : undefined,
            translate: isString ? undefined : value,
            alpha: 1,
            flexItem: { marginLeft: 6, marginRight: 6, marginTop: 2 }
        });
    }

    set icon(value: string) {
        this.Icon.patch({
            icon: value,
            w: this.Icon.w || 24
        });

        this.Label.patch({
            y: 2
        });
    }

    set iconWidth(value: number) {
        this.Icon.patch({
            w: value,
            h: value,
            iconWidth: value
        });
    }

    set useBorder(value: boolean) {
        this._isSecondary = value;

        this._createBackground();
    }

    set isActive(value: boolean) {
        this._isActive = value;

        this._createBackground();
    }

    set fontSize(value: number) {
        this.Label.patch({
            y: value > 14 ? 2 : 0,
            text: {
                fontSize: value
            }
        });
    }

    override _getFocused(): Lightning.Component | null | undefined {
        return this.Icon;
    }

    override _init() {
        this._createBackground();
        this.Content.patch({ w: this.w, h: this.h });
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    override _focus() {
        this._createBackground();
    }

    override _unfocus() {
        this._createBackground();
    }

    override _handleEnter() {
        if (this._id) {
            this.fireAncestors("$handleBtnClick", this._id);
        } else {
            return false;
        }
    }

    _getBtnColor() {
        const customTheme = themeManager.getTheme();

        // console.log("_getBtnColor", this._reverseColor, theme.colors.bg.primary);
        return this._disabled
            ? Colors(theme.colors.bg.light).get()
            : this._reverseColor
              ? Colors(theme.colors.bg.primary).get()
              : this.hasFocus() || this._isActive
                ? (customTheme?.button ?? Colors(theme.colors.focus).get())
                : Colors(theme.colors.bg.accent).get();
    }

    _createBackground() {
        const hasFocus = this.hasFocus();

        if (this._isSecondary) {
            const offset = 6;
            this.patch({
                Border: {
                    x: this._isSecondary ? offset * -1 : -1,
                    y: this._isSecondary ? offset * -1 : -1,
                    alpha: this._isSecondary ? (hasFocus ? 1 : 0) : 1,
                    texture: Lightning.Tools.getRoundRect(
                        this.w + (this._isSecondary ? offset + 1 : 0),
                        this._isSecondary ? this.h + offset + 1 : this.h,
                        theme.button.radius - 1,
                        this._isSecondary ? (hasFocus ? 3 : 0) : 1,
                        this._isSecondary ? this._getBtnColor() : theme.colors.text.primary,
                        false
                    )
                }
            });
        }

        this.patch({
            RoundedRectangle: {
                x: -1,
                y: -1,
                texture: Lightning.Tools.getRoundRect(
                    this.w,
                    this.h || theme.button.h,
                    theme.button.radius,
                    theme.button.border.enabled ? 1 : 0,
                    this._getBtnColor(),
                    hasFocus || this._isSecondary || !theme.button.border.enabled,
                    this._getBtnColor()
                ),
                alpha: this.hasFocus() || !this._isSecondary || this._isActive ? 1 : 0.75
            }
        });

        let textColor = this._reverseColor
            ? theme.colors.text.primary
            : hasFocus || this._isActive || this._isSecondary || !theme.button.border.enabled
              ? theme.colors.text.surfaceFocus
              : theme.colors.text.primary;
        this.Label.patch({
            text: {
                textColor: this._disabled
                    ? Colors(theme.colors.text.primary)
                          .alpha(this._disabled ? 0.4 : 1)
                          .get()
                    : theme.button.border.enabled && !hasFocus && !this._isActive && !this._isSecondary
                      ? this._getBtnColor()
                      : Colors(textColor).get()
            }
        });
    }

    _handleClick() {
        this.signal("handleClick");
        if (this._id) {
            this.fireAncestors("$handleBtnClick", this._id);
        }
    }
}
